/**
 * Bootstrap helpers — build AppServices from a concrete PlatformIO + EngineBinding
 * and wire the QueueManager to the queue store + telemetry bridge. The web and
 * desktop entrypoints call `createAppServices` with their platform/engine.
 */
import {
  QueueStore,
  createJsonClient,
  type JsonClient,
  type JsonClientOptions,
  type PlatformIO,
} from "@genz/platform";
import type { EngineBinding } from "@genz/engine-bind";
import { QueueManager } from "../queue/manager.js";
import { TelemetryClient } from "../telemetry/client.js";
import { useQueueStore, restoreConcurrency } from "../stores/queue.js";
import { useBatchStore, restoreSaveDir } from "../stores/batch.js";
import { useSessionStore, hydrateTokens, setSessionWarn, storedTokens } from "../stores/session.js";
import { getMockup } from "../stores/mockupStore.js";
import { resolveCaseImageBytes } from "../stores/caseImage.js";
import { signOutEverywhere } from "./signOut.js";
import { useReauthStore } from "./reauth.js";
import { startAutoSync, stopAutoSync } from "../sync/autoSync.js";
import { applyRenderBackend, getRenderBackend } from "../stores/renderBackend.js";
import { getPrimaryViewFallback } from "../stores/renderFallback.js";
import {
  convertTransparentFiles, savesWhenTheJobFinishes,
  type TransparentRow, type TransparentRunSpec,
} from "../transparent/run.js";
import { createRunWriter, type RunWriter } from "../export/runWriter.js";
import {
  MOCKUPS_BUNDLE_ROOT, TRANSPARENT_BUNDLE_ROOT, type ExportReport,
} from "../export/bundle.js";
import type { AppServices, AppLogger } from "./context.js";

export interface BootstrapOptions {
  io: PlatformIO;
  engine: EngineBinding;
  baseUrl: string;
  appVersion: string;
  concurrency?: number;
  /**
   * Hook forwarded to JsonClientOptions.onRequestObserved (Task 6 — client
   * observability). Wired by main.tsx to `logger.observeRequest` so every
   * network request is captured as a structured client-log event.
   */
  onRequestObserved?: JsonClientOptions["onRequestObserved"];
  /**
   * Client observability logger (Task 6). Passed here — in addition to being attached to
   * AppServices in main.tsx — so the auto-sync ticker (Wave-2 Task 5) can breadcrumb each
   * delivered template change. Optional: without it the ticker still runs, just unlogged.
   */
  logger?: AppLogger;
}

/**
 * Job ids whose terminal per-item results have already been mirrored to
 * telemetry. QueueManager.onChange re-fires for the whole job list on every
 * tick, so this guards against emitting duplicate `item` events for a job that
 * has already finished.
 */
const finalizedJobs = new Set<string>();

/**
 * WS-N review-1 I-1 — is this job a Transparent folder conversion?
 *
 * `useBatchStore`'s `expectedTotal` / `currentByJob` / `jobError` exist to reconcile the MOCKUP
 * Render button and the Batch tab's aggregate bar against `appendResult`, which a transparent
 * job never produces. Routing its `onProgress` / `onJobFailed` / `onJobExpecting` there anyway
 * painted the Batch tab's error banner on every pause or cancel of a conversion, and moved an
 * in-flight mockup run's `expectedTotal` — so the run's once-per-run auto-save fired a mockup
 * early and the last one arrived after the aggregate already read complete.
 *
 * The record is read from the queue store rather than tracked separately: `enqueue` emits
 * before any of the three callbacks can fire, so `runKind` is always there by then. A jobId the
 * store has never heard of is treated as a mockup — the pre-WS-N behaviour, which is the safe
 * default for a restored/legacy job.
 */
function isTransparentJob(jobId: string): boolean {
  return useQueueStore.getState().jobs.find((j) => j.jobId === jobId)?.runKind === "transparent";
}

/**
 * How long boot will wait for the engine's GPU-init summary before starting the app without it
 * (review M-1). Generous enough that a healthy machine always logs the line, short enough that a
 * driver wedged in `request_device` costs a delayed launch rather than no launch.
 */
export const COMPOSITOR_LOG_TIMEOUT_MS = 5_000;

/** Await `p`, or give up after `ms`. `undefined` (the command is absent) resolves immediately. */
async function withTimeout(p: Promise<void> | undefined, ms: number): Promise<void> {
  if (!p) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([p, new Promise<void>((resolve) => { timer = setTimeout(resolve, ms); })]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

export async function createAppServices(opts: BootstrapOptions): Promise<AppServices> {
  const client: JsonClient = createJsonClient({
    baseUrl: opts.baseUrl,
    // An expiry is NOT a sign-out (AUD-8 LB-12). It must still clear the identity — tokens,
    // buffered telemetry and the cache-first data (settings, options, admin lists) the next
    // account would otherwise see as their own — but the operator's ~1.5 GB of baked mockups
    // on this disk are still theirs and still valid, and deleting them buys nothing except a
    // 0.6-4.7 s-per-object re-download that can itself partially fail. `reason: "expired"`
    // is what makes that distinction; the same routine with the same deps otherwise.
    onAuthExpired: () => {
      // Say what happened, with the email prefilled, instead of silently presenting a blank
      // Login form to someone who was in the middle of a run.
      useReauthStore.getState().open(useSessionStore.getState().session?.email);
      void signOutEverywhere({
        io: opts.io, engine: opts.engine,
        log: opts.logger ? (level, message, ctx) => opts.logger?.log(level, message, ctx) : undefined,
        purgeBuffers: () => purgeAccountBuffers(),
      }, { reason: "expired" });
    },
    // Keep the persisted session's tokens in sync with the client's rotated ones
    // (transparent refresh) so a restart stays logged in. `undefined` (expiry/
    // logout) is handled by onAuthExpired/clear, so we only write real tokens.
    onTokensChanged: (tokens) => {
      if (tokens) useSessionStore.getState().setTokens(tokens);
    },
    readStoredTokens: storedTokens,
    onRequestObserved: opts.onRequestObserved,
    // WS-Q Q-8 / MIN-17: one line per session, and the only thing that will ever explain a
    // report of "it logs me out constantly on THIS machine" that is neither the announced
    // chain expiry (IMP-13) nor a transient failure misread as death (IMP-15).
    onClockSkew: (deltaMs) => {
      const seconds = Math.round(Math.abs(deltaMs) / 1000);
      const direction = deltaMs >= 0 ? "ahead of" : "behind";
      opts.logger?.log(
        Math.abs(deltaMs) < 60_000 ? "info" : "warn",
        `Clock · this machine is ${seconds}s ${direction} the server. A fast clock refreshes ` +
        "the session on every request (extra rotations, which raises the reuse-detection " +
        "risk); a slow one never refreshes proactively and takes the 401 path each time. " +
        "Check the system clock's automatic setting.",
        { skewMs: Math.round(deltaMs) },
      );
    },
  });

  // Stay logged in across restarts: a persisted session (zustand persist) carries
  // tokens, but the freshly-built client has none — hand them over so the first
  // authenticated request works (and any 401 transparently refreshes from here).
  // DESKTOP: first pull the device-encrypted secret-store copy over the localStorage one.
  // The module-load self-invoke of hydrateTokens() always no-oped in production — it runs
  // before main() calls setCurrentPlatform(), so currentPlatform() was still undefined and
  // the encrypted copy was WRITE-ONLY. Awaiting it HERE (platform is set by now) makes it a
  // real second restore source: if WKWebView's lazily-flushed localStorage lost the last
  // token rotation, the secret store still has it and the session survives the restart.
  //
  // WS-Q review-1 I-2: on desktop the encrypted store is now the ONLY place the refresh token
  // is written, so "could not read it" ends the session — and used to do so in total silence,
  // dropping the operator at a bare "Welcome back" with nothing to report and nothing to fix.
  // Route the store's swallowed failures into client observability, and turn the boot-time
  // case into a prompt that names the device store rather than blaming an expiry that may not
  // have happened.
  setSessionWarn((message, ctx) => opts.logger?.log("warn", `Session · ${message}`, ctx));
  const beforeHydrate = useSessionStore.getState().session;
  const hydrated = await hydrateTokens().catch(() => false);
  const restored = useSessionStore.getState().session;
  if (!hydrated && beforeHydrate && !restored) {
    opts.logger?.log(
      "warn",
      "Session · the saved sign-in on this device could not be read, so this launch starts " +
      "signed out. A corrupt or unreadable device secret store — not the session's age — is " +
      "what to check if this repeats on the same machine.",
      { email: beforeHydrate.email },
    );
    useReauthStore.getState().open(beforeHydrate.email, "unreadable-store");
  }
  if (restored?.tokens) client.setTokens(restored.tokens);

  const queueStore = new QueueStore(opts.io.localStore);
  const deviceId = await opts.io.deviceFingerprint();
  const tenantId = useSessionStore.getState().session?.tenantId ?? "unbound";

  const telemetry = new TelemetryClient({
    client,
    io: opts.io,
    deviceId,
    tenantId,
    appVersion: opts.appVersion,
    engineVersion: opts.engine.engineVersion,
    // F-9: where a heartbeat that stops itself says so. Without this the 403 loop was silent
    // in the log and loud in the console — exactly backwards.
    logger: opts.logger,
  });

  /**
   * WS-ERR H-2 — THE sign-out buffer purge, for every path.
   *
   * Each sign-out site used to compose its own from the LOGGER alone, so a deliberate sign-out
   * left the TELEMETRY buffer in place. The session-gained edge then calls `telemetry.start()`
   * (F-3, so a heartbeat stopped by one account's 403 comes back for the next), and `start()`
   * ends with a flush — so account A's buffered events went out under account B's token, and
   * the server attributes an event to whoever is authenticated when it arrives.
   *
   * One function, handed to every `signOutEverywhere` caller through `AppServices`, is what
   * stops the three call sites drifting apart again. The identity tag on each buffered row
   * (`TelemetryClient.setIdentity`) is the second, independent guard.
   */
  const purgeAccountBuffers = async (): Promise<void> => {
    // F-3: the heartbeat is a per-(device, account) claim. Stop it with the session rather than
    // letting a tick land under an identity that no longer exists; the session edge re-arms it.
    telemetry.stop();
    telemetry.setIdentity(null);
    await telemetry.purgeBuffer();
    await opts.logger?.purgeBuffers?.();
  };

  // The operator's render concurrency is device-local and MUST survive a restart: it used to
  // be in-memory only, so every launch silently reverted it to 1 (serial rendering) and the
  // Queue tab had to be re-set by hand. Restored BEFORE the manager is built so the manager and
  // the store agree from the first tick. Best-effort — never block boot on it.
  const storedConcurrency = await restoreConcurrency(opts.io.localStore).catch(() => undefined);
  // A host-supplied override is authoritative for THIS run — mirror it so the Queue tab shows
  // the truth. Written straight into the store (like `restoreConcurrency` does), NOT through
  // `setConcurrency`: that setter PERSISTS to device KV, so a host that ever passed a value
  // would silently overwrite the operator's stored choice — and keep overwriting it on every
  // boot, with no way to tell the two apart afterwards. An override is a runtime argument; the
  // persisted preference belongs to whoever moved the slider.
  if (opts.concurrency !== undefined) useQueueStore.setState({ concurrency: opts.concurrency });

  /**
   * WS-O / O-10 (D-36, the TRANSPARENT arm): ONE `RunWriter` per conversion.
   *
   * Keyed by jobId, which is where WS-N review-1 M-5's per-run claim map was keyed and for the
   * same reason: a paused conversion writes its segments into ONE run folder, and a per-CALL
   * guard let `Apple/Rose.png` (paused segment) and `Apple/rose.png` (resumed segment) be one
   * file on APFS/NTFS with the second silently destroying the first. The writer owns that map
   * now — one mechanism for both arms instead of a bespoke `Map<jobId, Map>` beside it. A
   * RETRY is a new jobId and therefore a clean map, exactly as before.
   *
   * Created in `runTransparent`, because that is the only callback that carries the SPEC: the
   * brand folder, the run folder and the destination are the conversion's, not the mockup
   * store's. Absent on WEB (`savesWhenTheJobFinishes`) — there is no folder in a browser, so
   * `onOutput` has nothing to write to and the tab's "Download (N files)" click stays the save
   * (WS-N review-1 M-4). Dropped when the job goes terminal, below.
   */
  const transparentWriters = new Map<string, RunWriter>();
  function transparentWriterFor(jobId: string, spec: TransparentRunSpec): RunWriter {
    let w = transparentWriters.get(jobId);
    if (!w) {
      w = createRunWriter({
        io: opts.io,
        logger: opts.logger,
        name: TRANSPARENT_BUNDLE_ROOT,
        destDir: spec.destDir,
        // WS-I / D-13: stamped when the conversion was ENQUEUED, so one run writes one folder.
        runFolder: spec.runFolder,
        // WS-I / D-11: the chosen brand is the top-level folder and the source's own subfolders
        // are kept beneath it. The rule `saveTransparentRows` applied, applied by the writer.
        prefix: spec.brandFolder,
      });
      transparentWriters.set(jobId, w);
    }
    return w;
  }

  /**
   * WS-O / D-36: ONE `RunWriter` per RUN — the run's output channel.
   *
   * Keyed by `batchId`, not by jobId, because "one run" is what the claim map has to span: a
   * multi-template "Create Mock-up" enqueues ONE queue of templates × designs (one JOB per
   * template) into ONE dated folder, and a per-job map would let two templates that resolve
   * onto the same path each claim it and the second silently overwrite the first — the exact
   * data loss `mockupParts.templateName` exists to prevent, re-opened at the job boundary. A
   * job with no `batchId` (a lone enqueue, a restored job) is its own run.
   *
   * Created lazily on the run's FIRST item (so a cancelled run never leaves an empty dated
   * folder behind), and dropped when the run's LAST job goes terminal, in `onChange` below,
   * beside where `transparentClaims` is dropped. Its serial queue is what `runBatch`'s
   * `await onItem` pushes back against.
   */
  const runWriters = new Map<string, RunWriter>();
  /** The run a job belongs to: its batch, or itself. Read from the queue store, which `enqueue`
   *  has already published by the time any callback can fire. */
  const runKeyOf = (jobId: string): string =>
    useQueueStore.getState().jobs.find((j) => j.jobId === jobId)?.batchId ?? jobId;
  /**
   * WS-O review-1 B-1: a SNAPSHOT of the writer's tally, for a panel to render.
   *
   * `RunWriter.report()` returns the writer's own mutable object, which it appends to per item
   * and releases when the run's last job goes terminal. A store holding that would hold a moving
   * target and then a disposed one — and zustand would never re-render, because the reference
   * never changes. Copying the four fields is what makes "800 files could not be written — …"
   * a thing the Batch panel and the Transparent tab can actually show.
   */
  const snapshot = (r: ExportReport): ExportReport => ({
    written: r.written,
    missing: [...r.missing],
    failed: [...r.failed],
    collisions: [...r.collisions],
    ...(r.cancelled ? { cancelled: r.cancelled } : {}),
    ...(r.destPath ? { destPath: r.destPath } : {}),
    ...(r.openError ? { openError: r.openError } : {}),
  });
  /**
   * ONE automatic reveal per RUN, remembered by run key rather than by writer identity.
   *
   * `RunWriter.finish()` refuses to reveal twice, but a run can own more than one writer.
   * `finishAndDispose` drops the map entry, and a LATE `onOutput` — the in-flight item of a
   * CANCELLED job, whose bytes arrive after the job went terminal — builds a fresh one through
   * `writerFor`, writes its file and is finished by the next terminal `onChange`. That is a
   * second file-manager window for one run, which is the defect this branch exists to remove
   * (review F-1, caught by `batch/write-through.e2e.test.tsx` case 7d).
   *
   * A key per run, held for the session: one short string per run the operator starts.
   */
  const revealedRuns = new Set<string>();
  /**
   * The run is over: reveal its folder, then release it.
   *
   * ONE helper for both arms, because "the run ended" means the same thing to both and the
   * ORDER is the whole point — `finish()` is chained onto the writer's own write queue, so it
   * runs after the last file's bytes, and `dispose()` is chained onto `finish()` so a closed
   * writer cannot swallow the reveal. `onChange` is not async and must not become so (it is a
   * store subscription), which is why this is a `void`ed chain and not an `await`.
   *
   * `finish()` never rejects — a refused reveal is a message on its report — so the `.then`
   * needs no rejection arm to keep `dispose()` reachable.
   */
  const finishAndDispose = (w: RunWriter | undefined, runKey: string): void => {
    if (!w) return;
    if (revealedRuns.has(runKey)) { w.dispose(); return; }
    revealedRuns.add(runKey);
    void w.finish().then(() => w.dispose());
  };
  function writerFor(jobId: string): RunWriter {
    const runKey = runKeyOf(jobId);
    let w = runWriters.get(runKey);
    if (!w) {
      /**
       * WS-O review-1 I-1: on WEB the writer IS the zip, and it is kept ALIVE past the run's
       * last terminal job (see `onChange`) so a `retryFailed` — a new jobId under the SAME run
       * key — re-joins the run's staged entries instead of starting an empty one. A run key the
       * app has never seen means a NEW run, and that is where the previous run's bytes go:
       * `clearOutputs()` has already dropped the store's `webZip` handle, so this drops the last
       * reference to them rather than accumulating one run's pixels per session.
       */
      if (opts.io.kind === "web") {
        for (const [key, stale] of runWriters) {
          if (key === runKey) continue;
          stale.dispose();
          runWriters.delete(key);
        }
      }
      const batch = useBatchStore.getState();
      w = createRunWriter({
        io: opts.io,
        logger: opts.logger,
        name: MOCKUPS_BUNDLE_ROOT,
        // The operator picked this BEFORE the run (`BatchPanel.enqueue`), which is what makes
        // the folder a precondition of the run rather than a post-run question. Web has none.
        destDir: opts.io.kind === "desktop" ? batch.saveDir : undefined,
        // WS-H1 / D-13: the run's dated folder, stamped by `clearOutputs()` when the run armed.
        runFolder: batch.runFolder,
        /**
         * WS-H2 (AUD-3 F-15 / LB-2), now fed per ITEM instead of once at the end of the run.
         *
         * The budget is UNCHANGED as a number and UNCHANGED as a mechanism
         * (`spillSavedOutputs`, `savedRoot`, `hydrateOutputBytes`). What changed is that
         * `savedRoot` exists from item 1, so the spill runs while the run is going and the peak
         * is the budget rather than the whole run. Desktop only: on web there is no file to
         * read back from, and breaking the Export tab is worse than holding the bytes.
         */
        onWritten: (root) => {
          if (opts.io.kind !== "desktop") return;
          const freed = useBatchStore.getState().spillSavedOutputs(root);
          if (freed > 0) {
            opts.logger?.log("info", "batch: spilled saved output bytes", {
              freedMb: Math.round(freed / 1024 / 1024), destPath: root,
            });
          }
        },
      });
      runWriters.set(runKey, w);
    }
    return w;
  }

  const queue = new QueueManager({
    engine: opts.engine,
    store: queueStore,
    tenantId,
    deviceId,
    // An explicit host override still wins; otherwise the operator's persisted choice does.
    concurrency: opts.concurrency ?? storedConcurrency ?? 1,
    // Spec 019 + Task B3: resolve a version's baked layered-mockup assets at render time,
    // keyed per view, so locked multi-view templates composite each view over ITS OWN shell
    // deterministically (else classic). The queue worker calls this per distinct view.
    resolveMockup: (versionId, view) => getMockup(versionId, view),
    // The CLASSIC (no-bake) arm's product: the view's case photo, from its published
    // `base_case_image_ref` (same download ladder as a baked asset) or this device's local image
    // store. Without it a template with masks and no bake exports the artwork on a bare canvas.
    resolveBaseImage: (templateId, versionId, view, ref) =>
      resolveCaseImageBytes(opts.engine, templateId, versionId, view, ref, {
        downloadBytes: opts.io?.downloadBytes?.bind(opts.io),
        onWarn: (message) =>
          opts.logger?.log("warn", `Template · ${message}`, { step: "caseImage", templateId }),
      }),
    // F-8: the queue's records are not reaching disk. The job's trace already carries it; this
    // is what puts it on the Queue page, where the operator is watching the run.
    onPersistFailed: (reason) => {
      useQueueStore.getState().setPersistDegraded(reason);
      opts.logger?.log("error", "Queue · this device is not saving the queue to disk — a restart will lose the run", {
        step: "queuePersist", error: reason,
      });
    },
    // WS-H1 (IM-6): read the preference PER JOB (a getter, not a value) so flipping the
    // Settings → Rendering toggle takes effect on the next render without a relaunch.
    allowPrimaryViewFallback: () => getPrimaryViewFallback(),
    // WS-N: the Transparent conversion, injected as a port so `queue/manager.ts` never imports
    // a canvas or a PlatformIO.
    //
    // WS-O / O-10: it no longer "converts, THEN saves". The writer is armed BEFORE the loop and
    // each ok row goes to disk the instant its bytes exist (`onOutput` → `writer.write`), so the
    // end-of-job `saveTransparentRows` call is gone: by the time the job finishes there is
    // nothing left to file. Same folder, same run folder, same run-scoped claim map, same
    // `export/bundle.ts` rules and the same two Error-log sentences — the only thing that moved
    // is WHEN.
    runTransparent: async (jobId, spec, hooks) => {
      const writer = savesWhenTheJobFinishes(opts.io.kind)
        ? transparentWriterFor(jobId, spec)
        : undefined;
      const live: TransparentRow[] = [];
      const rows = await convertTransparentFiles(opts.io, opts.logger, spec, {
        onRow: (i, n, row) => {
          // Mirror the rows into the queue store so the Transparent tab's table (scores + the
          // converted bytes) keeps filling live. They do NOT go onto the JobRecord — the queue
          // record stays pixel-free and telemetry-safe (see `transparentRows`).
          live.push(row);
          useQueueStore.getState().setTransparentRows(jobId, [...live]);
          // WS-O / D-36: RETURNED, not fire-and-forget — the manager's `onRow` carries the
          // write-through write, and `convertTransparentFiles` awaits what it returns.
          return hooks.onRow(i, n, row);
        },
        shouldAbort: hooks.shouldAbort,
      });
      useQueueStore.getState().setTransparentRows(jobId, rows);
      // WS-N review-1 M-4: on WEB the save is a zip DOWNLOAD, and a download fired from job
      // completion has no user gesture behind it. The Transparent tab offers
      // "Download (N files)" instead — the same rows, on the operator's click.
      if (!writer) return { rows };
      // Every ok row is already on disk. This only reports WHERE, for the job's trace line —
      // `lastSavedPath` was stamped by the first overlay, in `onOutput`, which is what makes
      // "Open folder" true from overlay 1 instead of from the end of the run.
      return { rows, ...(writer.destPath() ? { savedTo: writer.destPath() } : {}) };
    },
    onChange: (jobs) => {
      useQueueStore.getState().setJobs(jobs);
      // Forward metadata-only job records to telemetry (no pixels).
      for (const job of jobs) {
        void telemetry.emit({ type: "job", clientTs: Date.now(), data: job });
        // A terminal job no longer emits per-item progress, so drop its live entry
        // (Task 3) — the QueuePanel then falls back to the job's finished-items counts.
        if (job.status === "completed" || job.status === "failed" || job.status === "canceled") {
          useQueueStore.getState().clearLive(job.jobId);
          // …and the conversion's writer — with the cross-segment claim map review-1 M-5 added
          // — has nothing left to guard. A conversion is one job, so terminal is terminal here;
          // the MOCKUP writer below spans a multi-template run and needs the sibling check.
          //
          // `finish()` BEFORE `dispose()`: this is where the run ends, so this is where the
          // automatic reveal fires (the owner's "it should open when the mockups are
          // generated"). It is chained onto the writer's own queue, so `dispose()` landing on
          // the next line cannot overtake it — and the map `delete` is what keeps a re-fired
          // terminal status from calling either verb twice.
          finishAndDispose(transparentWriters.get(job.jobId), job.jobId);
          transparentWriters.delete(job.jobId);
          // WS-O: the RUN's writer owned the folder for the whole run. Drop it only when the
          // run's LAST job is terminal — a multi-template run's sibling jobs are still writing
          // into the same folder through the same claim map.
          const runKey = job.batchId ?? job.jobId;
          const stillRunning = jobs.some(
            (j) => (j.batchId ?? j.jobId) === runKey &&
              j.status !== "completed" && j.status !== "failed" && j.status !== "canceled",
          );
          // WS-O review-1 I-1: NOT on web. There the writer's `staged` is the run's only copy
          // of the bytes and "Download mock-ups (N)" reads through to it, so dropping the writer
          // here made `retryFailed` (a new jobId, the same run key) build a fresh writer with an
          // empty `staged` — the zip fell from the run's count to the retried segment's and the
          // run's own entries became unreachable. On web the writer is released when the NEXT
          // run arms, in `writerFor` above. On desktop the files are on disk and a fresh claim
          // map is exactly right, so nothing changes here.
          if (!stillRunning && opts.io.kind !== "web") {
            finishAndDispose(runWriters.get(runKey), runKey);
            runWriters.delete(runKey);
          }
        }
        // On a terminal status, also mirror each per-item result so the cloud
        // Activity/Error log has linked item rows (Loads(N), not Loads(0)). Guard
        // against re-emitting when onChange fires again for the same finished job.
        if ((job.status === "completed" || job.status === "failed") && !finalizedJobs.has(job.jobId)) {
          finalizedJobs.add(job.jobId);
          for (const item of job.items) {
            void telemetry.emit({ type: "item", clientTs: Date.now(), data: { ...item, jobId: job.jobId } });
          }
        }
      }
    },
    onTrace: (jobId, phase, level, message, itemKey) => {
      void telemetry.emit({
        type: "trace",
        clientTs: Date.now(),
        data: { jobId, itemKey, phase, startedAt: Date.now(), level, message },
      });
      // Render failures/warnings used to live ONLY in the trace table — invisible in the
      // Error log, the local ring, and offline ("it's not logging everything"). Mirror
      // error/warn traces into the client log so every render fault shows where the
      // operator actually looks.
      if (level === "error" || level === "warn") {
        opts.logger?.log(level, `Render · ${message}`, { jobId, phase, ...(itemKey ? { itemKey } : {}) });
      }
    },
    // Render bytes flow to the batch store (gallery/export) — NOT telemetry.
    // A multi-template batch enqueues ONE queue of templates × designs, so each
    // job's outputs ACCUMULATE onto the gallery (append, not replace) — the
    // single-selection flow appends a single job and reads the same way.
    // Feed BOTH consumers: the batch Gallery's AGGREGATE run bar (setProgress) and the
    // Queue page's per-job live bar/current-item (setLive, keyed by jobId — Task 3).
    onProgress: (jobId, p) => {
      // Pass jobId: the batch store keeps a per-job progress slot now. Without it every
      // running job wrote the SAME scalar, so with concurrency > 1 the aggregate bar
      // oscillated and failJob subtracted another job's count.
      // WS-N review-1 I-1: a transparent job's ticks stop here. Nothing ever REMOVES its
      // progress slot (only appendResult/failJob/addExpected do, and it produces none of
      // them), so the Batch tab read "N / 10" with zero mockups rendered.
      if (!isTransparentJob(jobId)) useBatchStore.getState().setProgress(p.completed, p.total, jobId);
      useQueueStore.getState().setLive(jobId, {
        completed: p.completed,
        total: p.total,
        current: p.current,
      });
    },
    // Task A4: reflect the job's async stage on the queue row. "preparing" fires before the
    // resolveMockup await (drives "Preparing assets…"); "rendering" on the first tick. Both
    // fire only within the running window, so this never resurrects a cleared live row.
    onPhase: (jobId, phase) => useQueueStore.getState().setPhase(jobId, phase),
    onResult: (jobId, result) => useBatchStore.getState().appendResult(result, jobId),
    /**
     * WS-O / D-36: every finished item, the instant its bytes exist, into the run folder.
     *
     * AWAITED all the way back to `runBatch`'s loop — that await is the memory bound. The
     * write never throws (a refused write is a report on `RunWriter.report()` and an Error-log
     * line), so a full disk at item 3 cannot abort items 4…1000.
     */
    onOutput: async (jobId, item) => {
      // BOTH arms, one channel. The conversion's writer was armed in `runTransparent` (it is
      // the only callback that carries the spec); its absence means WEB, where there is no
      // folder to write to and the tab's Download click is the save (WS-N review-1 M-4).
      const writer = item.kind === "transparent"
        ? transparentWriters.get(jobId)
        : writerFor(jobId);
      if (!writer) {
        // review-1 m-3: absent is NORMAL on web (there is no folder; the tab's Download click is
        // the save). Absent on DESKTOP is not reachable today — `runTransparent` arms the writer
        // before the loop — and if it ever becomes reachable it must not be a silently unwritten
        // overlay, which is the one shape this wave exists to remove.
        if (savesWhenTheJobFinishes(opts.io.kind)) {
          opts.logger?.log("warn", "Export · no run writer for this job — nothing was written", {
            jobId, kind: item.kind,
          });
        }
        return;
      }
      const outcome = await writer.write(item);
      /**
       * WS-O review-1 B-1: publish the run's tally where a PANEL can read it.
       *
       * Until now `report()` had exactly one consumer — the refusal throw's counter diff below —
       * so a run that refused 800 of 1000 writes told the operator nothing outside the Error log.
       * Published per item rather than at job-terminal so the count climbs while the run is going
       * (the operator sees the disk fill up at item 200, not at minute seven), and per ARM: the
       * conversion's goes to the tab that ran it, keyed by job, never to the mockup panel.
       */
      if (item.kind === "transparent") {
        useQueueStore.getState().setTransparentReport(jobId, snapshot(writer.report()));
      } else {
        useBatchStore.getState().setRunReport(snapshot(writer.report()));
      }
      if (opts.io.kind === "web") {
        // WS-O / O-8: there is no folder in a browser. The writer accumulates, the count climbs
        // live so the operator can watch the run fill, and the bytes are handed over only on a
        // CLICK (WS-N review-1 M-4 — a download fired from job completion has no user gesture
        // behind it). `entries` reads THROUGH to the writer: web cannot spill, so a second copy
        // of the bytes would double the run's peak.
        useBatchStore.getState().setWebZip({
          count: writer.pending().length,
          entries: () => writer.pending(),
        });
        return;
      }
      // LB-2: the run folder is knowable from item 1, not from the end of the run — this is
      // what "Open folder" is enabled on (O-5), and it is never a write COUNT. Stamped BEFORE
      // the refusal check below on purpose: a run whose every write was refused is exactly when
      // the operator most needs to open the (empty) folder and see why.
      const dest = writer.destPath();
      if (dest && useBatchStore.getState().lastSavedPath !== dest) {
        useBatchStore.getState().setLastSavedPath(dest);
      }
      // …and the same fact keyed by JOB, which is what a LIST of runs needs. `lastSavedPath` is
      // one slot for both arms, so after a conversion every Queue row's "Open folder" opened the
      // overlay folder (review F-4). `setJobSavedPath` is a no-op once the value is stamped, so
      // this is one store write per job, not one per item.
      if (dest) useBatchStore.getState().setJobSavedPath(jobId, dest);
      /**
       * The refusal THROW below is the mockup arm's only. `convertTransparentFiles` awaits
       * `hooks.onRow` OUTSIDE its per-file try/catch, so a throw here would not warn about one
       * overlay — it would abort the conversion and fail the whole job, which is the opposite
       * of the "a full disk at item 3 cannot abort items 4…1000" rule this channel exists for.
       * It would also buy nothing: `runTransparentJob` adds the row to its D-14 checkpoint
       * BEFORE it calls `onOutput` (`seen.add` precedes the emit), so a throw cannot un-check
       * it the way `runBatch`'s `onItem` await can. A refused overlay is what it already was —
       * a `collisions`/`failed` row on the writer's report and an `Export · …` line in the
       * Error log, emitted by the unchanged `export/bundle.ts`.
       */
      if (item.kind === "transparent") return;
      // WS-O / O-4: a REFUSED write must not be checked off. `RunWriter.write` never throws —
      // a failure is a report — so this is where "reported" becomes "the queue knows". Throwing
      // here takes `runBatch`'s warn path (which names the item and the reason) and leaves the
      // D-14 checkpoint unset, so a resume re-renders and re-writes it instead of skipping a
      // file that is not on disk. The item stays in the run's results either way.
      //
      // review-1 I-2: THIS item's own outcome, not a diff of the run's shared problem counter.
      // The writer is serialized, so at concurrency > 1 two jobs of one run both read the same
      // `before`; A's write failed, B's succeeded, and B threw A's path and reason for B's item.
      if (outcome.problem) {
        throw new Error(`${outcome.problem.ref}: ${outcome.problem.reason}`);
      }
    },
    // A whole-job engine throw never produces an onResult, so surface it to the batch
    // store: the Batch panel shows the error banner and releases the "Rendering…" button
    // instead of hanging forever.
    // WS-N review-1 I-1: a CONVERSION's sentence goes to the Transparent tab instead. It is
    // still surfaced — a paused/cancelled/unavailable conversion must never be silent — but
    // in the tab that ran it, not in the banner the mockup run owns.
    onJobFailed: (jobId, error, expectedItems) => {
      if (isTransparentJob(jobId)) {
        useQueueStore.getState().setTransparentNote(jobId, error);
        return;
      }
      useBatchStore.getState().failJob(error, expectedItems, jobId);
    },
    // WS-H1: a retried / resumed segment is about to render items the run total already
    // wrote off. Put them back, or the aggregate bar clamps their arrival away and the
    // once-per-run auto-save never fires for them.
    // WS-N review-1 I-1: a conversion never wrote into that total, so there is nothing to put
    // back — but "the rest is coming" is exactly when the Transparent tab's paused sentence
    // stops being true, so clear it here.
    onJobExpecting: (jobId, added) => {
      // review F-6: MORE ITEMS ARE COMING, so this run has not ended after all.
      //
      // `retryFailed` enqueues into the SAME run (`batchId: job.batchId ?? jobId`), so the run
      // key was already in `revealedRuns` and a finished "Retry 3 failed" opened nothing — the
      // operator started that work and waited for it, and its end is an end. Dropping the latch
      // here rather than in `writerFor` is deliberate: this hook fires only for a RETRY and a
      // RESUME, never for the late item of a cancelled job that F-1's second window came from.
      // (On a resume the latch is not set — a paused job is not terminal — so that is a no-op.)
      revealedRuns.delete(runKeyOf(jobId));
      revealedRuns.delete(jobId);
      if (isTransparentJob(jobId)) {
        useQueueStore.getState().setTransparentNote(jobId, null);
        return;
      }
      useBatchStore.getState().addExpected(added, jobId);
    },
  });

  await queue.restore();
  // Rehydrate the device-local save folder (Wave-2 T4) so the operator's chosen output
  // directory survives an app restart. Best-effort — never block boot on it.
  await restoreSaveDir(opts.io.localStore).catch(() => {});
  // WS-ERR — SEED THE IDENTITY FIRST. `start()`'s tail is a flush, and this call happens well
  // before `syncTicker` below runs, so the app's first flush of every launch used to run with
  // `identity` still null. `restoreSession` above has already put the persisted session in the
  // store, so the answer is available here; taking it makes the flush that is SUPPOSED to
  // deliver last run's offline events able to recognise them as this account's.
  //
  // NO TEST PINS THIS LINE, deliberately. `syncTicker()` runs synchronously a few lines below
  // and sets the same value, so the identity after `createAppServices` returns is "cust-A"
  // whether this line is here or not — a test would pass with it deleted, which is the kind of
  // test this wave exists to remove. What it actually narrows is the window INSIDE `start()`'s
  // flush, and observing that races the ticker.
  //
  // The load-bearing guard is therefore `readSendable`, which DEFERS rather than deletes while
  // the identity is null; that one is pinned, and mutation-checked, in
  // `telemetry/telemetry.test.ts`. This line is belt to those braces: it costs nothing, it
  // makes the common path correct without relying on the guard, and it is what a future caller
  // that starts telemetry from somewhere else will copy.
  telemetry.setIdentity(useSessionStore.getState().session?.customerId ?? null);
  // F-3: `syncTicker` below starts it on the session edge (including a restored session), and
  // `start()` is idempotent. This first call covers the pre-session window — the buffered
  // flush from a previous run, which `start()` also kicks off — and is harmless either way.
  telemetry.start();

  // Re-apply the per-device render-backend preference (Settings → Rendering). The native
  // pref is process-scoped, so a persisted "CPU only" choice must be pushed into the fresh
  // engine at every boot — otherwise it silently reverts to auto after a restart.
  applyRenderBackend(opts.engine, getRenderBackend());
  // D-12: populate `engine.backendId` before the FIRST render rather than after the operator
  // happens to visit Settings — the layered path's contentHash is scoped by it, and a batch
  // hashed as "cpu" on a GPU machine would be a second flavour of the same lie.
  void opts.engine.backendInfo?.().catch(() => { /* web / older build — backendId stays "cpu" */ });
  // WS-S / AUD-8 IMP-18: put the engine's GPU-init summary into the client log, ONCE, at boot.
  // `gpu.rs::init_context` prints `GENZ_GPU_INIT …` on every exit path, but an operator reporting
  // "GPU mode, only CPU" sends the Error log, not the app's stderr — and the engine has no log
  // channel to JS other than `genz_compositor_info`. So: one read, one `info` line through the
  // logger that every render trace already uses. No new command, no new event, no poll.
  // Awaited (not `void`ed) so the line is in the bundle before the first render can add to it;
  // absent on web and on older desktop builds, where it is simply skipped.
  //
  // Review M-1 — awaited, but never UNBOUNDEDLY. `compositor_info()` calls `context()` first, so
  // this read is what FORCES adapter enumeration and `request_device` (and, since the GL tier
  // landed, a second instance and a second enumeration when tier 1 fails). The population this
  // line exists for is precisely the one whose driver misbehaves in `request_device`: a driver
  // that hangs there used to hang a detached promise and would otherwise now stall
  // `createAppServices`, i.e. the app would not start at all — a diagnostic turning into an
  // outage. The timeout costs nothing when the engine answers (the race settles on the first
  // resolution), and the line is simply absent in the pathological case.
  await withTimeout(
    opts.engine
      .compositorInfo?.()
      .then((c) => {
        if (c.initSummary) opts.logger?.log("info", `Compositor · ${c.initSummary}`);
      })
      .catch(() => { /* web / older desktop build without the command — nothing to report */ }),
    COMPOSITOR_LOG_TIMEOUT_MS,
  );

  // Wave-2 Task 5: ONE app-wide template-revalidation ticker, gated on being logged in.
  // Start it after login and stop it on logout so template edits made on another device
  // propagate here within ≤60s (immediately on window focus) — no more manual-only Refresh.
  // The single-instance guard in startAutoSync makes a repeated "session present" safe, so
  // this covers BOTH a restored session on boot and a fresh login, and tears down on logout.
  const syncTicker = () => {
    if (useSessionStore.getState().session) {
      startAutoSync(client, { io: opts.io, logger: opts.logger });
      // H-2: whose session this is, set BEFORE `start()` — whose tail is a flush. Without the
      // tag in place first, that flush is exactly where a previous account's buffered events
      // would leave under this one's token.
      telemetry.setIdentity(useSessionStore.getState().session?.customerId ?? null);
      // F-3: re-arm the heartbeat for THIS account. A previous customer's explained 403 stops
      // the loop for the rest of that session — correctly — but the stop is per (device,
      // account) pair, and without this edge it silenced liveness for every later sign-in on
      // the machine until the app was restarted. `start()` is idempotent (it clears its own
      // timer first) and resets the block, the failure count and the backoff.
      telemetry.start();
      // F-10: a sign-out suspended durable logging (so the purge could not race its own
      // writes). A session exists again, so the stores are safe to write — turn it back on
      // BEFORE the flush, or the flush would no-op and nothing would ever resume it.
      opts.logger?.resumeDurableWrites?.();
      // Deliver anything captured BEFORE this login now, not up to 30s from now
      // (FLUSH_INTERVAL_MS) — a login-time failure is the one we most want in the cloud.
      void opts.logger?.flushNow?.();
    } else {
      stopAutoSync();
      // No session: nothing to report liveness FOR, and nothing is attributable. This is also
      // the sign-out edge, so the heartbeat stops here for a deliberate sign-out as well as an
      // expiry.
      telemetry.stop();
      telemetry.setIdentity(null);
    }
  };
  syncTicker(); // a persisted session that survived the restart starts the ticker immediately
  useSessionStore.subscribe(syncTicker);

  // AR-08: every path that ends a session clears the store, so this one edge cancels the
  // outgoing account's requests for all of them — sign-out, ending this device, expiry.
  let signedIn = !!useSessionStore.getState().session;
  useSessionStore.subscribe(({ session }) => {
    if (signedIn && !session) client.endSession();
    signedIn = !!session;
  });

  return {
    purgeAccountBuffers,
    io: opts.io,
    engine: opts.engine,
    client,
    queueStore,
    queue,
    telemetry,
    appVersion: opts.appVersion,
  };
}
